/**
 * Session expirée dans les tiroirs du planning (N21, QA A1-R-20), en fonctions
 * PURES exécutables sous `node --test`. Consommé par
 * `controllers/planning_frame_session_controller.js`.
 *
 * Dans ces tiroirs, toute réponse 422 de l'application re-rend le frame (erreurs
 * de validation) : un 422 SANS le frame est donc le refus CSRF d'une session
 * expirée (`protect_from_forgery` passe avant l'authentification, et le jeton est
 * lié à la session). S'y ajoutent le 401 (Devise, requête non navigationnelle) et
 * la page de connexion de la surface elle-même (redirection suivie par `fetch`).
 * Tout le reste (500, 404, autre page) garde le comportement par défaut de Turbo.
 */

// '/users/sign_in/' et '/users/sign_in' désignent la même page ; '/' reste '/'.
function normalizedPath(path) {
  const trimmed = path.replace(/\/+$/, '');
  return trimmed === '' ? '/' : trimmed;
}

// Pathname d'une url absolue, ou null quand elle est illisible.
function pathnameOf(url) {
  try {
    return new URL(url).pathname;
  } catch {
    return null;
  }
}

/**
 * @param {{ status?: number, url?: string } | null | undefined} response le
 *   `Response` natif de `turbo:frame-missing` (`detail.response`) ; seuls `status`
 *   et `url` sont lus.
 * @param {{ signInPath?: string }} [options] chemin de la page de connexion de la
 *   surface, fourni par le serveur (vide ⇒ seuls 401 et 422 comptent).
 * @returns {boolean} la réponse est-elle le visage d'une session expirée ?
 */
export function isSessionExpiredResponse(response, { signInPath } = {}) {
  if (!response) return false;
  if (response.status === 401 || response.status === 422) return true;
  if (!signInPath || typeof response.url !== 'string') return false;

  const landed = pathnameOf(response.url);
  if (landed === null) return false;
  return normalizedPath(landed) === normalizedPath(signInPath);
}

/**
 * Cible du lien « Se reconnecter » : la page courante, en chemin RELATIF
 * (pathname + search, sans ancre). Un GET navigationnel sans session passe par la
 * redirection Devise, qui mémorise la page et y ramène après la connexion.
 *
 * @param {string | undefined} currentUrl `location.href`
 * @returns {string} un chemin qui commence par UN seul « / » (jamais une url
 *   absolue ni « //hôte »), ou « / » quand l'url est illisible
 */
export function reconnectHref(currentUrl) {
  try {
    const url = new URL(currentUrl);
    // `http://h//evil.example/x` a pour pathname `//evil.example/x`, qui serait
    // un lien protocol-relative vers un hôte tiers : on replie les « / » de tête.
    return `${url.pathname.replace(/^\/+/, '/')}${url.search}`;
  } catch {
    return '/';
  }
}
